import { useCaseDocket, type DocketEntry } from "../../hooks/useCaseDocket";

/**
 * Sentinel Stream — external watch on the case's court-record activity, fed by
 * the NC eCourts Portal "follow the case" email alerts (ingested by the
 * sentinel pipeline). Newest activity first; unseen entries glow.
 */
function SentinelStream({ caseId }: { caseId?: string }) {
  const { data: entries, isLoading } = useCaseDocket(caseId);
  const list = entries ?? [];
  const unseen = list.filter((e) => !e.seen).length;

  return (
    <div className="flex flex-col rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-steel-dark/70 px-5 py-3 font-display text-base font-light uppercase tracking-[0.16em]">
        <span className="text-bone-dim">sentinel</span>
        <span className="flex items-center gap-2 text-bone-mute/70">
          <span className="inline-block h-1 w-1 animate-pulse-ember rounded-full bg-blue-bright" />
          {unseen > 0 ? `${unseen} new` : "standing watch"}
        </span>
      </div>
      <div className="flex flex-col gap-2 px-5 py-4">
        {isLoading && <p className="font-display text-sm text-bone-mute">checking the docket…</p>}

        {!isLoading && list.length === 0 && (
          <>
            <p className="font-display text-sm leading-relaxed text-bone-dim">
              No external docket activity yet.
            </p>
            <p className="mt-1 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/70">
              following: NC eCourts · Wake County
            </p>
          </>
        )}

        {list.slice(0, 12).map((e) => (
          <DocketRow key={e.id} entry={e} />
        ))}
      </div>
    </div>
  );
}

function DocketRow({ entry }: { entry: DocketEntry }) {
  return (
    <div className={`rounded border px-3 py-2 ${entry.seen ? "border-steel-dark/60 bg-iron/40" : "border-blue-bright/40 bg-iron-2/50"}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/70">
          {entry.entry_type ?? "docket"}
        </span>
        <span className="shrink-0 font-display text-sm text-bone-mute/60">{fmt(entry.entry_date)}</span>
      </div>
      <p className="mt-0.5 font-display text-sm leading-snug text-bone-dim">{entry.description}</p>
      {entry.external_ref && /^https?:/.test(entry.external_ref) && (
        <a href={entry.external_ref} target="_blank" rel="noreferrer"
          className="font-display text-sm text-blue-bright hover:underline">view on portal ▸</a>
      )}
    </div>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default SentinelStream;
